import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { 
  clientAPI, 
  contactAPI, 
  schoolAPI, 
  lookupAPI,
  staffAPI,
} from '../services/api';
import BasicInfoSection from './clientForm/sections/BasicInfoSection';
import ClientMedicaidSection from './clientForm/sections/ClientMedicaidSection';
import CurrentReferralSection from './clientForm/sections/CurrentReferralSection';
import ScAssignmentHistorySection from './clientForm/sections/ScAssignmentHistorySection';
import ClientInsuranceSummarySection from './clientForm/sections/ClientInsuranceSummarySection';
import AddressCommunicationSection from './clientForm/sections/AddressCommunicationSection';
import DemographicsSection from './clientForm/sections/DemographicsSection';
import DiagnosesSection from './clientForm/sections/DiagnosesSection';
import DsccSection from './clientForm/sections/DsccSection';
import ContactsProvidersSection from './clientForm/sections/ContactsProvidersSection';
import { referralAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from './ContentHeader';
import { formatPediatricianPipeLabelFromClient, formatSpecialistPipeLabelFromClient } from '../utils/providerDisplayLabel';
import './ClientForm.css';

function ClientForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantPath } = useTenantFromUrl();
  const isEdit = !!id;
  const fromNewClientReferral = !isEdit && searchParams.get('from') === 'new-client-referral';

  const NEW_CLIENT_DRAFT_KEY = 'cfc_new_client_draft';

  // Initialize form data with all fields (snake_case to match DB). Defaults for new client match contact defaults.
  const [formData, setFormData] = useState({
    display_id: null,
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gestation_week: '40',
    ei_number: '',
    participant_id: '',
    medicaid_rin: '',
    has_medicaid: false,
    assigned_staff_id: '',
    street_1: '',
    street_2: '',
    city_id: '',
    state: 'IL',
    zip: '',
    county_id: '',
    race_id: '',
    gender: '',
    primary_language_id: '',
    preferred_language_id: '',
    hearing_status: 'hearing',
    interpretation_status: 'not_needed',
    hispanic: false,
    school_id: '',
    pediatrician_id: '',
    pediatrician_site_id: '',
    specialist_id: '',
    specialist_site_id: '',
    last_contacted_at: '',
    last_contact_method: '',
    sc_note: '',
    communication_note: '',
    diagnosis_1_id: '',
    diagnosis_2_id: '',
    diagnosis_3_id: '',
    dscc_status_id: '',
    dscc_location_provider_id: '',
    dscc_location_site: '',
    dscc_migrated_uncertain: false,
  });

  // Client contacts: array of { contact_id, relationship_id, is_primary, sort_order, contact? }
  const [clientContacts, setClientContacts] = useState([{ contact_id: '', relationship_id: '', is_primary: true, sort_order: 0 }]);

  // Lookup data
  const [contacts, setContacts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [pediatricianDisplayLabel, setPediatricianDisplayLabel] = useState('');
  const [specialistDisplayLabel, setSpecialistDisplayLabel] = useState('');
  const [staff, setStaff] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [races, setRaces] = useState([]);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [diagnosisAutoeligible, setDiagnosisAutoeligible] = useState([]);
  const [dsccStatuses, setDsccStatuses] = useState([]);
  const [dsccOfficeLabel, setDsccOfficeLabel] = useState('');
  const [currentReferral, setCurrentReferral] = useState(null);
  const [insuranceSummaryPayload, setInsuranceSummaryPayload] = useState(null);
  const [insuranceOpen, setInsuranceOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [pendingScChange, setPendingScChange] = useState(null);
  const [confirmGenerateDocs, setConfirmGenerateDocs] = useState(true);
  const originalAssignedStaffIdRef = useRef(null);
  const generateDocsRef = useRef(true);

  // Merge selected contacts into dropdown list (contacts API is paginated; selected may not be on page 1)
  const primaryContactRow = useMemo(() => {
    const list = clientContacts || [];
    return list.find((r) => r.is_primary) || list[0] || null;
  }, [clientContacts]);

  const contactsForDropdown = useMemo(() => {
    const base = [...(contacts || [])];
    const hasId = (cid) => base.some((c) => String(c.id) === String(cid));
    (clientContacts || []).forEach((link) => {
      if (link.contact_id && link.contact) {
        const c = {
          id: link.contact_id,
          first_name: link.contact.first_name,
          last_name: link.contact.last_name,
          email: link.contact.email || '',
          phone: link.contact.phone || '',
          street_1: link.contact.street_1 ?? '',
          city: link.contact.city ?? '',
        };
        if (!hasId(link.contact_id)) {
          base.push(c);
        }
      }
    });
    return base;
  }, [contacts, clientContacts]);

  useEffect(() => {
    loadOptions();
    if (isEdit) {
      loadClient();
      loadCurrentReferral();
    }
  }, [id]);

  // Restore new-client draft when returning from contact/provider creation (or browser back)
  useEffect(() => {
    if (id) return;
    try {
      const raw = sessionStorage.getItem(NEW_CLIENT_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.formData) {
        const { phone: _legacyP, email: _legacyE, comment: legacyComment, ...restDraft } = draft.formData;
        setFormData((prev) => ({
          ...prev,
          ...restDraft,
          sc_note: restDraft.sc_note != null && restDraft.sc_note !== '' ? restDraft.sc_note : (legacyComment || prev.sc_note || ''),
        }));
      }
      if (Array.isArray(draft?.clientContacts) && draft.clientContacts.length > 0) {
        setClientContacts(draft.clientContacts);
      }
      sessionStorage.removeItem(NEW_CLIENT_DRAFT_KEY);
    } catch (_) {
      sessionStorage.removeItem(NEW_CLIENT_DRAFT_KEY);
    }
  }, [id]);

  const loadOptions = async () => {
    setLoadingOptions(true);
    const loadData = async (apiCall, name, setter) => {
      try {
        const response = await apiCall();
        const data = response?.data?.data || response?.data || [];
        const list = Array.isArray(data) ? data : [];
        setter(list);
        return list;
      } catch (error) {
        console.error(`Error loading ${name}:`, error.response?.data || error.message);
        setter([]);
        return [];
      }
    };
    const langsPromise = loadData(() => lookupAPI.getLanguages(), 'languages', setLanguages);
    await Promise.allSettled([
      langsPromise,
      loadData(() => contactAPI.getAll(), 'contacts', setContacts),
      loadData(() => schoolAPI.getAll(), 'schools', setSchools),
      loadData(() => staffAPI.getAll({ active: 'true' }), 'staff', setStaff),
      loadData(() => lookupAPI.getRaces(), 'races', setRaces),
      loadData(() => lookupAPI.getCounties(), 'counties', setCounties),
      loadData(() => lookupAPI.getCities(), 'cities', setCities),
      loadData(() => lookupAPI.getRelationships(), 'relationships', setRelationships),
      loadData(() => lookupAPI.getDiagnosisAutoeligible(), 'diagnosis (auto-eligible)', setDiagnosisAutoeligible),
      loadData(() => lookupAPI.getDsccStatuses(), 'DSCC statuses', setDsccStatuses),
    ]);
    const langResult = await langsPromise;
    setLoadingOptions(false);
    // Default Preferred Language and Primary Language to English when adding a new client
    if (!id && langResult?.length) {
      const english = langResult.find((l) => (l.description || '').trim().toLowerCase() === 'english');
      if (english) {
        setFormData((prev) =>
          prev.primary_language_id === '' && prev.preferred_language_id === ''
            ? { ...prev, primary_language_id: String(english.id), preferred_language_id: String(english.id) }
            : prev
        );
      }
    }
  };

  const loadClient = async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getById(id);
      const client = response.data.data;

      // Map client data to form state (handle dates, nulls, etc.). display_id is for UI only.
      setFormData({
        display_id: client.display_id,
        first_name: client.first_name || '',
        middle_name: client.middle_name || '',
        last_name: client.last_name || '',
        date_of_birth: client.date_of_birth ? client.date_of_birth.split('T')[0] : '',
        gestation_week: client.gestation_week ?? '',
        ei_number: client.ei_number || '',
        participant_id: client.participant_id || '',
        medicaid_rin: client.medicaid_rin || '',
        has_medicaid: client.has_medicaid === true,
        assigned_staff_id: client.assigned_staff_id || '',
        street_1: client.street_1 || '',
        street_2: client.street_2 || '',
        city_id: client.city_id || '',
        state: client.state || '',
        zip: client.zip || '',
        county_id: client.county_id || '',
        race_id: client.race_id || '',
        gender: client.gender || '',
        primary_language_id: client.primary_language_id || '',
        preferred_language_id: client.preferred_language_id ?? '',
        hearing_status: client.hearing_status || '',
        interpretation_status: client.interpretation_status || '',
        hispanic: client.hispanic === true,
        school_id: client.school_id || '',
        pediatrician_id: client.pediatrician_id || '',
        pediatrician_site_id: (() => {
          const raw = client.pediatrician_payee_site_id ?? client.pediatrician_site_id ?? '';
          if (raw !== '' && raw != null) return String(raw);
          if (client.pediatrician_id) return `provider:${client.pediatrician_id}`;
          return '';
        })(),
        specialist_id: client.specialist_id || '',
        specialist_site_id: (() => {
          const raw = client.specialist_payee_site_id ?? client.specialist_site_id ?? '';
          if (raw !== '' && raw != null) return String(raw);
          if (client.specialist_id) return `provider:${client.specialist_id}`;
          return '';
        })(),
        last_contacted_at: client.last_contacted_at ? client.last_contacted_at.split('T')[0] : '',
        last_contact_method:
          client.last_contact_method === 'in_person' ? 'meeting' : client.last_contact_method || '',
        sc_note: client.sc_note != null && client.sc_note !== '' ? client.sc_note : (client.comment || ''),
        communication_note: client.communication_note != null && client.communication_note !== '' ? client.communication_note : '',
        diagnosis_1_id: client.diagnosis_1_id != null ? String(client.diagnosis_1_id) : '',
        diagnosis_2_id: client.diagnosis_2_id != null ? String(client.diagnosis_2_id) : '',
        diagnosis_3_id: client.diagnosis_3_id != null ? String(client.diagnosis_3_id) : '',
        dscc_status_id: client.dscc_status_id != null ? String(client.dscc_status_id) : '',
        dscc_location_provider_id: client.dscc_location_provider_id || '',
        dscc_location_site: String(client.dscc_location_payee_site_id ?? client.dscc_location_provider_site_id ?? ''),
        dscc_migrated_uncertain: client.dscc_migrated_uncertain === true,
      });
      // Client contacts from API (client_contacts). Primary phone/email live on the contact, not clients.phone/email.
      // If legacy client row still has phone/email but primary contact does not, pre-fill contact in state so first save syncs DB.
      const contactsList = Array.isArray(client.contacts)
        ? client.contacts.map((link) => ({
            contact_id: link.contact_id || '',
            relationship_id: link.relationship_id || '',
            is_primary: !!link.is_primary,
            sort_order: link.sort_order ?? 0,
            contact: link.contact || null,
          }))
        : [];
      let nextContacts = contactsList.length > 0 ? contactsList : [{ contact_id: '', relationship_id: '', is_primary: true, sort_order: 0 }];
      const primIdx = nextContacts.findIndex((r) => r.is_primary);
      const pi = primIdx >= 0 ? primIdx : 0;
      const row = nextContacts[pi];
      if (row?.contact_id && row?.contact) {
        const c = row.contact;
        const hasClientPhone = String(client.phone || '').trim();
        const hasClientEmail = String(client.email || '').trim();
        const cPhoneEmpty = !String(c.phone || '').trim();
        const cEmailEmpty = !String(c.email || '').trim();
        if ((cPhoneEmpty && hasClientPhone) || (cEmailEmpty && hasClientEmail)) {
          nextContacts = [...nextContacts];
          nextContacts[pi] = {
            ...row,
            contact: {
              ...c,
              phone: cPhoneEmpty && hasClientPhone ? client.phone : (c.phone || ''),
              email: cEmailEmpty && hasClientEmail ? client.email : (c.email || ''),
            },
          };
        }
      }
      setClientContacts(nextContacts);
      originalAssignedStaffIdRef.current = client.assigned_staff_id || '';
      setPediatricianDisplayLabel(formatPediatricianPipeLabelFromClient(client));
      setDsccOfficeLabel((client.dscc_location_label || '').trim());
      setSpecialistDisplayLabel(formatSpecialistPipeLabelFromClient(client));
    } catch (error) {
      console.error('Error loading client:', error);
      alert('Failed to load client');
      navigate(tenantPath('/lists/clients'));
    } finally {
      setLoading(false);
    }
  };

  const onMedicaidFieldChange = useCallback(({ name, value }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (isEdit) setSaveTrigger((p) => p + 1);
  }, [isEdit]);

  const handleChange = (e) => {
    const { name, type } = e.target;
    let value = type === 'checkbox' ? e.target.checked : e.target.value;
    if (name === 'medicaid_rin' && type !== 'checkbox') {
      value = String(value).replace(/\D/g, '').slice(0, 9);
    }
    if (isEdit && name === 'assigned_staff_id' && String(value) !== String(originalAssignedStaffIdRef.current ?? '')) {
      setConfirmGenerateDocs(true);
      setPendingScChange(value);
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
      ...(name === 'pediatrician_id' ? { pediatrician_site_id: '' } : {}),
      ...(name === 'specialist_id' ? { specialist_site_id: '' } : {}),
    });
    if (isEdit) setSaveTrigger((p) => p + 1);
  };

  const handlePrimaryContactPhoneEmailChange = useCallback((field, value) => {
    setClientContacts((prev) => {
      const next = [...(prev || [])];
      const primIdx = next.findIndex((r) => r.is_primary);
      const pi = primIdx >= 0 ? primIdx : 0;
      const row = next[pi];
      if (!row?.contact_id) return prev;
      const contact = { ...(row.contact || {}), id: row.contact_id, [field]: value };
      next[pi] = { ...row, contact };
      return next;
    });
    if (isEdit) setSaveTrigger((p) => p + 1);
  }, [isEdit]);

  const handlePediatricianOfficeChange = useCallback(
    ({ value, label, providerId }) => {
      const v = value != null ? String(value) : '';
      const isProviderSynthetic = v.startsWith('provider:');
      setFormData((prev) => ({
        ...prev,
        pediatrician_site_id: v,
        pediatrician_id:
          providerId !== '' && providerId != null
            ? String(providerId)
            : isProviderSynthetic
              ? v.replace(/^provider:/, '')
              : '',
      }));
      setPediatricianDisplayLabel((label || '').trim());
      if (isEdit) setSaveTrigger((p) => p + 1);
    },
    [isEdit]
  );

  const handleSpecialistOfficeChange = useCallback(
    ({ value, label, providerId }) => {
      const v = value != null ? String(value) : '';
      const isProviderSynthetic = v.startsWith('provider:');
      setFormData((prev) => ({
        ...prev,
        specialist_site_id: v,
        specialist_id:
          providerId !== '' && providerId != null
            ? String(providerId)
            : isProviderSynthetic
              ? v.replace(/^provider:/, '')
              : '',
      }));
      setSpecialistDisplayLabel((label || '').trim());
      if (isEdit) setSaveTrigger((p) => p + 1);
    },
    [isEdit]
  );

  const setClientContactsAndTrigger = useCallback((arg) => {
    setClientContacts(arg);
    if (isEdit) setSaveTrigger((p) => p + 1);
  }, [isEdit]);

  const saveNewClientDraft = useCallback((data, contacts) => {
    try {
      sessionStorage.setItem(NEW_CLIENT_DRAFT_KEY, JSON.stringify({
        formData: data,
        clientContacts: contacts ?? [{ contact_id: '', relationship_id: '', is_primary: true, sort_order: 0 }],
      }));
    } catch (_) {
      // ignore storage errors
    }
  }, []);

  const handleSelectContact = useCallback((index, contact) => {
    setClientContacts((prev) => {
      const next = [...(prev || [])];
      if (!next[index]) return prev;
      const isPrimary = next[index].is_primary || index === 0;
      next[index] = {
        ...next[index],
        contact_id: contact.id,
        contact: {
          id: contact.id,
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          phone: contact.phone,
          street_1: contact.street_1,
          street_2: contact.street_2,
          city: contact.city,
          city_id: contact.city_id,
          state: contact.state,
          zip: contact.zip,
          county_id: contact.county_id,
        },
      };
      // Sync address to formData so the read-only address section displays
      // correctly and create-mode validation passes.
      if (isPrimary) {
        setFormData((prev) => ({
          ...prev,
          street_1: contact.street_1 || '',
          street_2: contact.street_2 || '',
          city: contact.city || '',
          city_id: contact.city_id != null ? String(contact.city_id) : '',
          state: contact.state || prev.state || '',
          zip: contact.zip || '',
          county_id: contact.county_id != null ? String(contact.county_id) : '',
        }));
      }
      return next;
    });
  }, [setFormData]);

  const loadCurrentReferral = async () => {
    if (!id) return;
    try {
      const response = await referralAPI.getByClientId(id);
      const referrals = response.data.data || [];
      // Get the most recent referral (sorted by open_date DESC, first one)
      // This shows the latest referral even if it's closed
      const latest = referrals.length > 0 ? referrals[0] : null;
      setCurrentReferral(latest);
    } catch (error) {
      console.error('Error loading current referral:', error);
      setCurrentReferral(null);
    }
    try {
      const ins = await clientAPI.getReferralInsuranceSummary(id);
      setInsuranceSummaryPayload(ins.data?.data ?? null);
    } catch (error) {
      console.error('Error loading referral insurance summary:', error);
      setInsuranceSummaryPayload(null);
    }
  };

  const performSave = useCallback(async () => {
    const contactsPayload = (clientContacts || [])
      .filter((row) => row.contact_id)
      .map((row, idx) => ({
        contact_id: row.contact_id ? String(row.contact_id) : null,
        relationship_id: row.relationship_id ? parseInt(row.relationship_id, 10) : null,
        is_primary: row.is_primary,
        sort_order: row.sort_order ?? idx,
      }));
    const hasPrimary = contactsPayload.some((c) => c.is_primary);
    if (contactsPayload.length > 0 && !hasPrimary) {
      contactsPayload[0].is_primary = true;
    }

    // Strict legacy-style required checks on create; edit mode keeps autosave permissive for existing partial records.
    if (!isEdit) {
      const normalize = (value) => (value == null ? '' : String(value).trim());
      const missing = [];
      const requiredFields = [
        ['first_name', 'First Name'],
        ['last_name', 'Last Name'],
        ['date_of_birth', 'Date of Birth'],
        ['street_1', 'Street Address 1'],
        ['city_id', 'City'],
        ['state', 'State'],
        ['zip', 'ZIP Code'],
        ['county_id', 'County'],
        ['race_id', 'Race'],
        ['gender', 'Gender'],
        ['primary_language_id', 'Primary Language'],
      ];
      requiredFields.forEach(([key, label]) => {
        if (!normalize(formData[key])) missing.push(label);
      });
      if (contactsPayload.length === 0) {
        missing.push('Contact one');
        missing.push('Relationship');
      } else {
        const primaryContact = contactsPayload.find((c) => c.is_primary) || contactsPayload[0];
        if (!primaryContact?.contact_id) missing.push('Contact one');
        if (!primaryContact?.relationship_id) missing.push('Relationship');
      }
      if (missing.length > 0) {
        const err = new Error(`Missing required fields: ${missing.join(', ')}`);
        err.isValidationError = true;
        throw err;
      }
    }

    const genDocs = generateDocsRef.current;
    generateDocsRef.current = true;
    const submitData = {
      ...formData,
      generate_documents_on_assign: genDocs,
      school_id: formData.school_id ? String(formData.school_id).trim() || null : null,
      pediatrician_id: formData.pediatrician_id ? String(formData.pediatrician_id).trim() || null : null,
      ...(() => {
        const raw = formData.pediatrician_site_id;
        if (raw == null || raw === '' || String(raw).startsWith('provider:')) {
          return { pediatrician_site_id: null, pediatrician_payee_site_id: null };
        }
        const siteKey = typeof raw === 'number' ? String(raw) : String(raw).trim();
        return { pediatrician_site_id: siteKey, pediatrician_payee_site_id: siteKey };
      })(),
      specialist_id: formData.specialist_id ? String(formData.specialist_id).trim() || null : null,
      ...(() => {
        const raw = formData.specialist_site_id;
        if (raw == null || raw === '' || String(raw).startsWith('provider:')) {
          return { specialist_site_id: null, specialist_payee_site_id: null };
        }
        const siteKey = typeof raw === 'number' ? String(raw) : String(raw).trim();
        return { specialist_site_id: siteKey, specialist_payee_site_id: siteKey };
      })(),
      assigned_staff_id: formData.assigned_staff_id ? parseInt(formData.assigned_staff_id) : null,
      race_id: formData.race_id ? parseInt(formData.race_id) : null,
      primary_language_id: formData.primary_language_id ? parseInt(formData.primary_language_id) : null,
      preferred_language_id: formData.preferred_language_id ? parseInt(formData.preferred_language_id, 10) : null,
      hearing_status: formData.hearing_status || null,
      interpretation_status: formData.interpretation_status || null,
      ei_number: formData.ei_number || null,
      participant_id: formData.participant_id || null,
      medicaid_rin:
        formData.medicaid_rin && String(formData.medicaid_rin).replace(/\D/g, '').length === 9
          ? String(formData.medicaid_rin).replace(/\D/g, '')
          : null,
      has_medicaid: !!formData.has_medicaid,
      gender: formData.gender || null,
      date_of_birth: formData.date_of_birth || null,
      last_contacted_at: formData.last_contacted_at || null,
      last_contact_method:
        formData.last_contact_method === 'in_person'
          ? 'meeting'
          : formData.last_contact_method || null,
      sc_note: formData.sc_note ? String(formData.sc_note).trim() || null : null,
      communication_note: formData.communication_note ? String(formData.communication_note).trim() || null : null,
      gestation_week: formData.gestation_week ? parseInt(formData.gestation_week, 10) : null,
      hispanic: !!formData.hispanic,
      diagnosis_1_id: formData.diagnosis_1_id ? String(formData.diagnosis_1_id).trim() || null : null,
      diagnosis_2_id: formData.diagnosis_2_id ? String(formData.diagnosis_2_id).trim() || null : null,
      diagnosis_3_id: formData.diagnosis_3_id ? String(formData.diagnosis_3_id).trim() || null : null,
      dscc_status_id: formData.dscc_status_id ? parseInt(formData.dscc_status_id, 10) : null,
      dscc_location_provider_id: formData.dscc_location_provider_id ? String(formData.dscc_location_provider_id).trim() || null : null,
      ...(() => {
        const raw = formData.dscc_location_site;
        if (raw == null || raw === '' || String(raw).startsWith('provider:')) {
          return { dscc_location_payee_site_id: null, dscc_location_provider_site_id: null };
        }
        const siteKey = typeof raw === 'number' ? String(raw) : String(raw).trim();
        return { dscc_location_payee_site_id: siteKey, dscc_location_provider_site_id: siteKey };
      })(),
      contacts: contactsPayload,
    };
    // helper-only / backend-managed fields — not client columns to send directly
    delete submitData.dscc_location_site;
    delete submitData.dscc_migrated_uncertain;
    delete submitData.phone;
    delete submitData.email;
    delete submitData.comment;
    delete submitData.street_1;
    delete submitData.street_2;
    delete submitData.city_id;
    delete submitData.state;
    delete submitData.zip;
    delete submitData.county_id;

    let createdClient = null;
    if (isEdit) {
      await clientAPI.update(id, submitData);
    } else {
      const res = await clientAPI.create(submitData);
      createdClient = res.data?.data ?? null;
    }

    // Address/communication fields are read-only on the client form — edit via the eye icon on the contact.

    if (!isEdit) return createdClient;
  }, [formData, clientContacts, isEdit, id]);

  useAutoSave(saveTrigger, performSave, { enabled: isEdit, delayMs: 500 });

  const clientSectionNavItems = useMemo(() => {
    const items = [{ id: 'client-section-basic-info', label: 'Basic' }];
    if (!isEdit) {
      items.push({ id: 'client-section-client-medicaid', label: 'Medicaid' });
    }
    if (isEdit) {
      items.push({ id: 'client-section-current-referral', label: 'Referral' });
      items.push({ id: 'client-section-insurance', label: 'Insurance' });
    }
    items.push(
      { id: 'client-section-address', label: 'Address' },
      { id: 'client-section-demographics', label: 'Demographics' },
      { id: 'client-section-diagnoses', label: 'Diagnoses' },
      { id: 'client-section-contacts', label: 'Contacts' },
      { id: 'client-section-school-providers', label: 'School & providers' }
    );
    return items;
  }, [isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const created = await performSave();
      try {
        sessionStorage.removeItem(NEW_CLIENT_DRAFT_KEY);
      } catch (_) {
        // ignore
      }
      if (!isEdit && fromNewClientReferral && created?.id) {
        navigate(tenantPath(`/referral/new?clientId=${created.id}&from=new-client-referral`));
      } else {
        navigate(tenantPath('/lists/clients'));
      }
    } catch (error) {
      console.error('Error saving client:', error);
      const message = error.response?.data?.error || error.message || 'Failed to save client';
      alert(message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <div className="loading">Loading form...</div>;
  }

  if (loading && isEdit) {
    return <div className="loading">Loading client...</div>;
  }

  return (
    <div className="client-form">
      {!isEdit && (
        <div className="form-header">
          <div>
            <h1>Add New Client</h1>
          </div>
        </div>
      )}
      {isEdit && (
        <ContentHeader title="Edit Client" />
      )}

      <form onSubmit={handleSubmit}>
        <nav className="client-form-section-nav" aria-label="On this page">
          <span className="client-form-section-nav-label">On this page</span>
          <ul className="client-form-section-nav-list">
            {clientSectionNavItems.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="client-form-section-nav-link">
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div id="client-section-basic-info" className="client-form-anchor-target">
          <BasicInfoSection
            formData={formData}
            handleChange={handleChange}
            staff={staff}
            canAssignStaff={hasPermission('clients.assign_staff')}
          />
        </div>

        {!isEdit && (
          <div id="client-section-client-medicaid" className="client-form-anchor-target">
            <ClientMedicaidSection
              hasMedicaid={!!formData.has_medicaid}
              medicaidRin={formData.medicaid_rin || ''}
              onMedicaidFieldChange={onMedicaidFieldChange}
              disabled={false}
              permissionHint={null}
            />
          </div>
        )}

        {isEdit && (
          <div id="client-section-current-referral" className="client-form-anchor-target">
            <CurrentReferralSection
              currentReferral={currentReferral}
              clientId={id}
              tenantPath={tenantPath}
              onRefreshReferral={loadCurrentReferral}
            />
            <ScAssignmentHistorySection clientId={id} />
          </div>
        )}

        {isEdit && (
          <div id="client-section-insurance" className="client-form-anchor-target">
            <div className="client-form-section-collapse-bar">
              <span className="client-form-section-collapse-label">Medicaid &amp; insurance</span>
              <button
                type="button"
                className="section-collapse-toggle"
                onClick={() => setInsuranceOpen((v) => !v)}
              >
                {insuranceOpen ? 'Hide' : 'Show'}
              </button>
            </div>
            {insuranceOpen && (
              <>
                <ClientMedicaidSection
                  hasMedicaid={!!formData.has_medicaid}
                  medicaidRin={formData.medicaid_rin || ''}
                  onMedicaidFieldChange={onMedicaidFieldChange}
                  disabled={!hasPermission('clients.update')}
                  permissionHint={
                    !hasPermission('clients.update')
                      ? 'You do not have permission to edit the client record; Medicaid fields are read-only here.'
                      : null
                  }
                />
                {insuranceSummaryPayload?.referral_id && insuranceSummaryPayload?.summary ? (
                  <ClientInsuranceSummarySection
                    clientId={id}
                    tenantPath={tenantPath}
                    summaryPayload={insuranceSummaryPayload}
                  />
                ) : null}
              </>
            )}
          </div>
        )}

        <div id="client-section-address" className="client-form-anchor-target">
          <AddressCommunicationSection
            formData={formData}
            handleChange={handleChange}
            counties={counties}
            cities={cities}
            primaryContactPhone={primaryContactRow?.contact?.phone ?? ''}
            primaryContactEmail={primaryContactRow?.contact?.email ?? ''}
            onPrimaryContactPhoneEmailChange={handlePrimaryContactPhoneEmailChange}
            hasPrimaryContact={!!primaryContactRow?.contact_id}
            canEditPrimaryContactComm={hasPermission('contacts.update')}
          />
        </div>

        <div id="client-section-demographics" className="client-form-anchor-target">
          <DemographicsSection
            formData={formData}
            handleChange={handleChange}
            races={races}
            languages={languages}
          />
        </div>

        <div id="client-section-diagnoses" className="client-form-anchor-target">
          <DiagnosesSection
            formData={formData}
            handleChange={handleChange}
            diagnosisOptions={diagnosisAutoeligible}
          />
          <DsccSection
            formData={formData}
            handleChange={handleChange}
            dsccStatuses={dsccStatuses}
            dsccOfficeLabel={dsccOfficeLabel}
            onDsccOfficeChange={({ providerId, providerSiteId }) => {
              setFormData((f) => ({
                ...f,
                dscc_location_provider_id: providerId || '',
                dscc_location_site: providerSiteId || '',
              }));
              setDsccOfficeLabel('');
              if (isEdit) setSaveTrigger((p) => p + 1);
            }}
          />
        </div>

        <div id="client-section-contacts" className="client-form-anchor-target">
          <ContactsProvidersSection
            formData={formData}
            handleChange={handleChange}
            onPediatricianOfficeChange={handlePediatricianOfficeChange}
            pediatricianDisplayLabel={pediatricianDisplayLabel}
            onSpecialistOfficeChange={handleSpecialistOfficeChange}
            specialistDisplayLabel={specialistDisplayLabel}
            clientContacts={clientContacts}
            setClientContacts={isEdit ? setClientContactsAndTrigger : setClientContacts}
            onSelectContact={handleSelectContact}
            onBeforeNavigateToNewContact={!isEdit ? saveNewClientDraft : undefined}
            onBeforeNavigateToNewProvider={!isEdit ? saveNewClientDraft : undefined}
            searchContacts={(q) => contactAPI.getAll({ q: (q || '').trim(), page: 1, pageSize: 500 }).then((res) => res.data?.data ?? [])}
            contacts={contactsForDropdown}
            schools={schools}
            relationships={relationships}
            clientId={isEdit ? id : null}
            tenantPath={tenantPath}
          />
        </div>


        <div className="form-actions">
          {isEdit ? null : (
            <>
              <button
                type="button"
                onClick={() => navigate(fromNewClientReferral ? tenantPath('/lists/create-client-referral') : tenantPath('/lists/clients'))}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-create">
                {loading ? 'Saving...' : fromNewClientReferral ? 'Save and add referral' : 'Create Client'}
              </button>
            </>
          )}
        </div>
      </form>

      {pendingScChange !== null && (() => {
        const newStaff = (staff || []).find((s) => String(s.id) === String(pendingScChange));
        const scName = newStaff
          ? [newStaff.first_name, newStaff.last_name].filter(Boolean).join(' ')
          : 'the selected SC';
        const clientName = [formData.first_name, formData.last_name].filter(Boolean).join(' ') || 'this client';
        return (
          <div className="modal-overlay">
            <div className="modal-content sc-assign-confirm-modal">
              <h3>SC Assignment</h3>
              <p className="modal-description">
                Assign <strong>{scName}</strong> to <strong>{clientName}</strong>?
              </p>
              <label className="sc-assign-confirm-check">
                <input
                  type="checkbox"
                  checked={confirmGenerateDocs}
                  onChange={(e) => setConfirmGenerateDocs(e.target.checked)}
                />
                Generate assignment documents
              </label>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setPendingScChange(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-save"
                  onClick={() => {
                    generateDocsRef.current = confirmGenerateDocs;
                    originalAssignedStaffIdRef.current = pendingScChange;
                    setFormData((prev) => ({ ...prev, assigned_staff_id: pendingScChange }));
                    setSaveTrigger((p) => p + 1);
                    setPendingScChange(null);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default ClientForm;
